import {inject} from '@angular/core';

import {withDevtools} from '@angular-architects/ngrx-toolkit';
import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {tapResponse} from '@ngrx/operators';

import {pipe, switchMap, tap} from 'rxjs';

import {Dimension, Measure} from '@selpia/shared/models';
import {RequestStatus} from '@selpia/shared/data-access';

import {AppConfigService} from '../data-services/app-config.service';
import {ParametersHelper, DimensionHelper} from '../utils';
import {AppParameters} from '../models/parameters.model';

export interface AppConfigState extends AppParameters {
  dimensions: Dimension[] | undefined;
  loadingStatus: RequestStatus;
  dashboardTitle: string;
}

const initialAppConfigState: AppConfigState = {
  dimensions: undefined,
  initialFocusDimensionLevel: undefined,
  isAIAssistantEnabled: false,
  periods: [],
  initialPeriod: undefined,
  initialDate: undefined,
  initialFocusMeasure: undefined,
  focusMeasures: [],
  growthMeasureLabel: undefined,
  gridTitle: undefined,
  mainTag: undefined,
  tileTable: undefined,
  report: undefined,
  highlightStrategies: [],
  loadingStatus: RequestStatus.Resolved,
  dashboardTitle: "10-sec Executive Board",
};

export const AppConfigStore = signalStore(
  {providedIn: 'root'},
  withDevtools('app-config'),
  withState(initialAppConfigState),
  withMethods((store, appConfigService = inject(AppConfigService)) => ({
    loadConfig: rxMethod<Measure[]>(
      pipe(
        tap(() => patchState(store, {loadingStatus: RequestStatus.Pending})),
        switchMap((allMeasures) => {
          return appConfigService.getConfig()
            .pipe(
              tapResponse({
                next: ([dimensions, parameters]) =>
                  patchState(store, {
                    dimensions: DimensionHelper.enrichWithCode(dimensions),
                    ...ParametersHelper.getInitialParams(dimensions, parameters, allMeasures),
                    loadingStatus: RequestStatus.Resolved,
                  }),
                error: () =>
                  patchState(store, {loadingStatus: RequestStatus.Rejected}),
              }),
            );
        }),
      ),
    ),
  })),
);

export type AppConfigStore = InstanceType<typeof AppConfigStore>;
